import { Injector } from '@angular/core';
import { assertDefined } from '../../../../cross-team-shared/misc-utils/strict-record';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../team-apis/core/shell-connector';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { CuboidFace } from '../../../render/mesh/cuboid/cuboid-helper';
import { rotateSelectedFace } from '../../../state/cuboid-location-state-3d';
import { PointCloudContentStore } from '../../../state/point-cloud-content-store';
import { getShapeForEventTracking } from '../../../utils/misc-utils';
import { Axis } from '../edit-panel.component';
import { NdcBoundingBox, isPointInsideBox } from './drag-handler-utils';

export class FoiSelectorHandler {
  protected readonly store: PointCloudContentStore;
  protected readonly shellConnector: ShellConnector;

  constructor(
    protected axis: Axis,
    injector: Injector,
  ) {
    this.store = injector.get(PointCloudContentStore);
    this.shellConnector = injector.get(shellConnectorToken);
  }

  updateCuboidFace(selectedFace: CuboidFace) {
    const focusedShapeStore = this.store.getFocusedShapeStore();
    assertDefined(focusedShapeStore);

    focusedShapeStore('key_locations').setUsing((locations) =>
      locations.map((location) => rotateSelectedFace(location, selectedFace)),
    );

    const eventTrackingPanelName = {
      2: 'top',
      1: 'middle',
      0: 'bottom',
    };

    this.shellConnector.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      method: 'edit_panel_select_face',
      shapes: [getShapeForEventTracking(focusedShapeStore.state())],
      panel: eventTrackingPanelName[this.axis],
    });
  }
}

export function getCuboidFaceFoiSelector(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
  axis: Axis,
) {
  if (axis === 0) {
    return getSidePanelSelectedFace(containerPoint, boundingBox);
  } else if (axis === 1) {
    return getBackPanelSelectedFace(containerPoint, boundingBox);
  }

  return getTopPanelSelectedFace(containerPoint, boundingBox);
}

function getSidePanelSelectedFace(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
) {
  return isPointInsideBox(containerPoint, leftEdgeSelector(boundingBox))
    ? CuboidFace.Back
    : undefined;
}

function getBackPanelSelectedFace(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
) {
  if (isPointInsideBox(containerPoint, leftEdgeSelector(boundingBox))) {
    return CuboidFace.Left;
  }
  return isPointInsideBox(containerPoint, rightEdgeSelector(boundingBox))
    ? CuboidFace.Right
    : undefined;
}

function getTopPanelSelectedFace(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
) {
  if (isPointInsideBox(containerPoint, leftEdgeSelector(boundingBox))) {
    return CuboidFace.Left;
  } else if (isPointInsideBox(containerPoint, rightEdgeSelector(boundingBox))) {
    return CuboidFace.Right;
  } else if (
    isPointInsideBox(containerPoint, bottomEdgeSelector(boundingBox))
  ) {
    return CuboidFace.Back;
  }

  return undefined;
}

const outsideFrustum = 1.5;
const edgeDistanceBuffer = 0.05;

function leftEdgeSelector({ min, max }: NdcBoundingBox) {
  const leftMin: Point2D = [-outsideFrustum, min[1]];
  const leftMax: Point2D = [min[0] - edgeDistanceBuffer, max[1]];
  return { min: leftMin, max: leftMax };
}

function rightEdgeSelector({ min, max }: NdcBoundingBox) {
  const rightMin: Point2D = [max[0] + edgeDistanceBuffer, min[1]];
  const rightMax: Point2D = [outsideFrustum, max[1]];
  return { min: rightMin, max: rightMax };
}

function bottomEdgeSelector({ min, max }: NdcBoundingBox) {
  const bottomMin: Point2D = [min[0], -outsideFrustum];
  const bottomMax: Point2D = [max[0], min[1] - edgeDistanceBuffer];
  return { min: bottomMin, max: bottomMax };
}
